import { BehaviorSubject, debounceTime, Observable, Subject, tap } from 'rxjs';
import { inject, Injectable, signal } from '@angular/core';
import { AthleticUser, AnetAuthService, AliveAuthService } from '@athleticnet/athletic-live-api';
import { AthleticFollowInfo } from '../models/athletic-follow-info.interface';
import { AnetLiveService } from '../services/anet-live.service';
import { Notice } from '../../models/notice/notice.interface';
import { AliveNoticeService } from '../../services/alive-notice.service';
import { DeliveryMethod } from '../../models/notice/delivery-method.interface';
import { AliveFollowService } from '../../services/alive-follow.service';
import { MeetList } from '@athleticnet/athletic-live-api-models';
import { AthleteDetailCacheService } from '../../services/athlete-detail-cache.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * AthleticLIVE-specific auth/user-context service.
 *
 * Core federated Athletic.net authentication (refresh token cookie, access
 * token exchange, `GetUserData` hydration, login URL, sign out) lives in
 * `AnetAuthService` from `@athleticnet/athletic-live-api`. This service wraps
 * it and adds AthleticLIVE business logic: follow info, notices, delivery
 * methods, and AthleticLIVE roles.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly anetAuthService = inject(AnetAuthService);
  private readonly aliveAuthService = inject(AliveAuthService);
  private readonly anetLiveService = inject(AnetLiveService);
  private readonly aliveFollowService = inject(AliveFollowService);
  private readonly aliveNoticeService = inject(AliveNoticeService);
  private readonly athleteDetailCacheService = inject(AthleteDetailCacheService);

  currentFollowInfo: AthleticFollowInfo | null = null;

  /** The current Athletic.net user (from the shared AnetAuthService). */
  public readonly updateUser: Observable<AthleticUser | null> = this.anetAuthService.user$;

  private _updateFollowInfo: BehaviorSubject<AthleticFollowInfo | null> =
    new BehaviorSubject<AthleticFollowInfo | null>(null);
  public readonly updateFollowInfo: Observable<AthleticFollowInfo | null> = this._updateFollowInfo.asObservable();

  private _updateNotices: BehaviorSubject<Notice[]> = new BehaviorSubject<Notice[]>([]);
  public readonly updateNotices: Observable<Notice[]> = this._updateNotices.asObservable();

  private _updateDeliveryMethods: BehaviorSubject<DeliveryMethod[]> = new BehaviorSubject<DeliveryMethod[]>([]);
  public readonly updateDeliveryMethods: Observable<DeliveryMethod[]> = this._updateDeliveryMethods.asObservable();

  private readonly _aliveRoles = signal<string[]>([]);
  public readonly aliveRoles = this._aliveRoles.asReadonly();

  signedIn$ = this.anetAuthService.signedIn$;
  signingOut$ = this.anetAuthService.signingOut$;

  private _newUserData = new Subject<any>();
  newUserData$ = this._newUserData.asObservable();

  constructor() {
    this.updateFollowInfo.pipe(takeUntilDestroyed(), debounceTime(100)).subscribe((followInfo) => {
      if (followInfo && followInfo.athletes) {
        const athleteIds = followInfo.athletes.map((a) => a.athleticLiveId);
        this.athleteDetailCacheService.updateAthleteList(athleteIds);
      }
    });
  }

  async signOut() {
    await this.anetAuthService.signOut();
  }

  async setUser() {
    await this.anetAuthService.refreshUser();
  }

  async setFollowInfo(meet: MeetList) {
    let athleticNetIdArr: any[] = [];
    if (meet.athleticNetId) {
      athleticNetIdArr = [meet.athleticNetId];
    }
    if (athleticNetIdArr[0] === -1) {
      athleticNetIdArr = meet.athleticNetMeets.map((anetMeet: any) => {
        return anetMeet.athleticNetId;
      });
    }

    let anetFollowInfo = await this.anetLiveService.getFollowInfo(meet.sport, athleticNetIdArr.join(','));
    anetFollowInfo = await this.aliveFollowService.addAliveIdsToAnetFollows(meet.id, anetFollowInfo);
    let aliveFollowInfo = await this.aliveFollowService.getFollows(meet.id);
    if (anetFollowInfo) {
      anetFollowInfo.aliveFollows = aliveFollowInfo;
    } else {
      anetFollowInfo = {
        athletes: [],
        teamFollowType: '',
        teams: [],
        aliveFollows: aliveFollowInfo,
      };
    }
    if (this.hasNewFollowInfo(anetFollowInfo)) {
      this.currentFollowInfo = anetFollowInfo;
      this._updateFollowInfo.next(anetFollowInfo);
    }
  }

  protected hasNewFollowInfo(anetFollowInfo: AthleticFollowInfo | null) {
    if (anetFollowInfo === null && this.currentFollowInfo !== null) {
      return true;
    }
    if (this.currentFollowInfo === null && anetFollowInfo !== null) {
      return true;
    }
    return (
      anetFollowInfo &&
      this.currentFollowInfo &&
      !(
        anetFollowInfo.athletes.length === this.currentFollowInfo.athletes.length &&
        anetFollowInfo.teams.length === this.currentFollowInfo.teams.length &&
        anetFollowInfo.aliveFollows.length === this.currentFollowInfo.aliveFollows.length
      )
    );
  }

  async setNotices(meetId: number) {
    const data = await this.aliveNoticeService.getNotices(meetId);
    if (data) {
      this._updateNotices.next(data);
    } else {
      this._updateNotices.next([]);
    }
  }

  async setDeliveryMethods() {
    const data = await this.aliveNoticeService.getDeliveryMethods();
    if (data) {
      this._updateDeliveryMethods.next(data);
    } else {
      this._updateDeliveryMethods.next([]);
    }
  }

  /**
   * Trades the refresh token for a new access token (core flow handled by
   * AnetAuthService), then refreshes AthleticLIVE-specific state.
   */
  GetNewAccessToken(): Observable<string> {
    return this.anetAuthService.getNewAccessToken().pipe(
      tap(async (accessToken) => {
        if (accessToken) {
          await this.setUser();
          await this.setDeliveryMethods();
          await this.setAliveRoles();
        }
      }),
    );
  }

  getAccessToken(): string {
    return this.anetAuthService.getAccessToken();
  }

  getRefreshToken(): string {
    return this.anetAuthService.getRefreshToken();
  }

  public storeRefreshToken(refreshToken: string): void {
    this.anetAuthService.storeRefreshToken(refreshToken);
  }

  private async setAliveRoles() {
    const data = await this.aliveAuthService.getMyRoles();
    if (data) {
      this._aliveRoles.set(data);
    } else {
      this._aliveRoles.set([]);
    }
  }
}
